import { useCallback, useEffect, useRef, useState } from 'react';

export type AudioPlaybackStatus = 'idle' | 'loading' | 'playing' | 'paused';

export interface AudioPlayback {
  readonly status: AudioPlaybackStatus;
  readonly currentTime: number;
  readonly duration: number;
  readonly toggle: () => Promise<void>;
  readonly seek: (seconds: number) => void;
}

interface UseAudioPlaybackOptions {
  /** Resolve a playable URL. Called once, on the first play. */
  readonly load: () => Promise<string>;
  /** Called when loading or starting playback fails. */
  readonly onError: (error: unknown) => void;
}

/**
 * Plays one audio source on demand. Nothing is fetched until the first play,
 * so a thread full of recordings costs nothing until someone listens.
 *
 * The URL from `load` is treated as a blob URL owned by this hook and is
 * revoked on unmount. Pressing the button again while the file is still
 * loading cancels: a load that settles after a cancel or an unmount is
 * released rather than played, so nothing plays without its controls.
 */
export const useAudioPlayback = ({
  load,
  onError,
}: UseAudioPlaybackOptions): AudioPlayback => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  // Bumped on cancel and unmount; a load that resolves under an older value
  // is stale.
  const loadGenerationRef = useRef(0);
  const unmountedRef = useRef(false);
  const [status, setStatus] = useState<AudioPlaybackStatus>('idle');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      loadGenerationRef.current += 1;
      audioRef.current?.pause();
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }
    };
  }, []);

  const createAudio = (url: string): HTMLAudioElement => {
    urlRef.current = url;

    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
    audio.addEventListener('timeupdate', () =>
      setCurrentTime(audio.currentTime)
    );
    audio.addEventListener('play', () => setStatus('playing'));
    audio.addEventListener('pause', () => setStatus('paused'));
    audio.addEventListener('ended', () => setStatus('paused'));
    audioRef.current = audio;
    return audio;
  };

  /** Fetch and build the element, or `null` if cancelled or unmounted meanwhile. */
  const loadAudio = async (): Promise<HTMLAudioElement | null> => {
    const generation = ++loadGenerationRef.current;
    setStatus('loading');

    let url: string;
    try {
      url = await load();
    } catch (error) {
      if (generation === loadGenerationRef.current) {
        throw error;
      }
      return null;
    }

    if (generation !== loadGenerationRef.current) {
      URL.revokeObjectURL(url);
      return null;
    }
    return createAudio(url);
  };

  const toggle = async () => {
    if (status === 'loading') {
      loadGenerationRef.current += 1;
      setStatus('idle');
      return;
    }

    const existing = audioRef.current;
    if (existing && !existing.paused) {
      existing.pause();
      return;
    }

    try {
      const audio = existing ?? (await loadAudio());
      if (audio) {
        await audio.play();
      }
    } catch (error) {
      if (unmountedRef.current) {
        return;
      }
      setStatus(audioRef.current ? 'paused' : 'idle');
      onError(error);
    }
  };

  const seek = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = seconds;
      setCurrentTime(seconds);
    }
  }, []);

  return { status, currentTime, duration, toggle, seek };
};
